// Generated from build58 src/features/musicEdition/personaData.ts; do not edit.
import { editionGenres } from './model';
// Use the classifier's structured evidence, not guessed thresholds or mock data.
export function buildPersonaData(t, e, en, genre) {
  const l = (ko, english) => (en ? english : ko);
  const key = t.persona?.key ?? t.ratingStyle;
  const messages = t.persona?.evidenceMessages ?? [];
  const params = (code) => messages.find((m) => m.code === `taste.evidence.${code}`)?.params ?? {};
  const number = (p, k) => (p[k]?.trim() && Number.isFinite(Number(p[k])) ? Number(p[k]) : NaN);
  const split = (a, b, x, y, basis, highlightLeft = false) =>
    Number.isFinite(x) && Number.isFinite(y) && x >= 0 && y >= 0 && x + y > 0
      ? {
          kind: 'split',
          rows: [
            { label: a, value: x },
            { label: b, value: y },
          ],
          basis,
          highlightLeft,
        }
      : undefined;
  if (['OVERSEAS', 'DOMESTIC', 'BORDERLESS'].includes(key)) {
    const kr = t.regions.find((r) => ['KR', '국내'].includes(r.label)),
      global = t.regions.find((r) => ['GLOBAL', '해외'].includes(r.label));
    return split(
      l('국내', 'Korean'),
      l('해외', 'Global'),
      kr?.count ?? 0,
      global?.count ?? 0,
      l('국내·해외로 분류된 평가 앨범 기준', 'Rated albums with a known music region'),
      key === 'DOMESTIC',
    );
  }
  if (['EARLY_ADOPTER', 'CATALOG_DIGGER'].includes(key)) {
    const early = key === 'EARLY_ADOPTER',
      p = params(early ? 'earlyAdopter' : 'catalogDigger');
    const total = number(p, 'datedCount'),
      count = number(p, early ? 'freshReleaseCount' : 'catalogCount');
    return split(
      early ? l('그 이후', 'Later') : l('15년 미만', 'Under 15 years'),
      early ? l('발매 30일 이내', 'Within 30 days') : l('15년 이상', '15+ years'),
      total - count,
      count,
      l(
        `날짜를 확인할 수 있는 ${total}장 · 평가 당시 기준`,
        `${total} dated ${total === 1 ? 'album' : 'albums'} · age when rated`,
      ),
    );
  }
  if (['ALBUM_PERSON', 'TRACK_PICKER'].includes(key)) {
    const p = params(key === 'ALBUM_PERSON' ? 'albumPerson' : 'trackPicker');
    return key === 'ALBUM_PERSON'
      ? split(
          l('싱글·EP', 'Singles / EPs'),
          l('정규 앨범', 'Albums'),
          number(p, 'singleCount'),
          number(p, 'albumCount'),
          l('작품 유형을 확인할 수 있는 평가 기준', 'Rated releases with a known type'),
        )
      : split(
          l('정규 앨범', 'Albums'),
          l('싱글·EP', 'Singles / EPs'),
          number(p, 'albumCount'),
          number(p, 'singleCount'),
          l('작품 유형을 확인할 수 있는 평가 기준', 'Rated releases with a known type'),
        );
  }
  if (['STRICT', 'GENEROUS'].includes(key)) {
    const rows = Array.from({ length: 5 }, (_, i) => ({ label: `${i + 1}`, value: 0 }));
    for (const [score, count] of Object.entries(t.ratingDistribution)) {
      const rating = Number(score);
      if (
        Number.isFinite(rating) &&
        rating >= 0 &&
        rating <= 5 &&
        Number.isFinite(count) &&
        count >= 0
      )
        rows[Math.max(0, Math.ceil(rating) - 1)].value += count;
    }
    const total = rows.reduce((sum, r) => sum + r.value, 0);
    return total
      ? {
          kind: 'rating',
          rows,
          average: t.averageRating,
          basis: l(
            `평가 ${total}장 · 0.5점 단위 별점을 1점 구간으로 묶은 분포`,
            `${total} ${total === 1 ? 'rating' : 'ratings'} · Half-point scores grouped into one-point bands`,
          ),
        }
      : undefined;
  }
  if (['GENRE_LOYAL', 'OMNIVORE'].includes(key)) {
    const rows = t.topGenres
      .filter((g) => editionGenres([g.label]).length && g.count > 0)
      .slice(0, 4)
      .map((g) => ({ label: genre(g.label), value: g.count }));
    return rows.length
      ? {
          kind: 'rows',
          rows,
          basis: l(
            '평가 앨범의 장르 등장 횟수 · 복수 장르 포함',
            'Genre appearances on rated albums · multiple tags included',
          ),
        }
      : undefined;
  }
  if (key === 'ARTIST_LOYAL') {
    const p = params('artistLoyal'),
      count = number(p, 'count'),
      total = number(p, 'ratingCount');
    return p.artist &&
      Number.isFinite(count) &&
      Number.isFinite(total) &&
      total > 0 &&
      count >= 0 &&
      count <= total
      ? {
          kind: 'rows',
          rows: [{ label: p.artist, value: count }],
          total,
          basis: l(
            `전체 ${total}장 중 ${count}장 · ${((count / total) * 100).toFixed(1)}%`,
            `${count} of ${total} rated ${total === 1 ? 'album' : 'albums'} · ${((count / total) * 100).toFixed(1)}%`,
          ),
        }
      : undefined;
  }
  if (key === 'CONTRARIAN') {
    const records = e.records.filter(
      (r) =>
        typeof r.album.editionCommunityAverage === 'number' &&
        Number.isFinite(r.album.editionCommunityAverage) &&
        (r.album.editionCommunityCount ?? 0) > 0,
    );
    if (!records.length) return undefined;
    const mean = (fn) => records.reduce((sum, r) => sum + fn(r), 0) / records.length;
    return {
      kind: 'compare',
      rows: [
        { label: l('나', 'Me'), value: mean((r) => r.review.rating) },
        {
          label: l('다른 회원', 'Other members'),
          value: mean((r) => r.album.editionCommunityAverage),
        },
      ],
      difference: mean((r) => Math.abs(r.review.rating - r.album.editionCommunityAverage)),
      basis: l(
        `비교 가능한 ${records.length}장 · 나를 제외한 회원 평균과 비교`,
        `${records.length} comparable ${records.length === 1 ? 'album' : 'albums'} · other members only`,
      ),
    };
  }
  return {
    kind: 'summary',
    rows: [
      { label: l('평가한 작품', 'Rated releases'), value: t.ratingCount },
      { label: l('평균 별점', 'Average rating'), value: t.averageRating },
    ],
    basis: l('지금까지 쌓인 평가 기록', 'Your rating history so far'),
  };
}
